import { Field, FieldContent, FieldGroup, FieldLabel, Input } from "@beweegbaas/ui";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { Page } from "#/components/page";
import {
  ImageCropperField,
  type ImageCropValue,
} from "#/modules/school/components/image-cropper-field";
import {
  getSchoolDetail,
  submitUpdateAlias,
  submitUpdateLogo,
} from "#/modules/school.detail/detail.functions";
import { m } from "#/paraglide/messages";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/$school/detail/info")({
  loader: ({ params }) => getSchoolDetail({ data: { school: params.school } }),
  component: SchoolInfoPage,
});

function SchoolInfoPage() {
  const { school: schoolData, canEdit } = Route.useLoaderData();
  const router = useRouter();
  const [logoValue, setLogoValue] = useState<ImageCropValue | null>(null);
  const [alias, setAlias] = useState(schoolData?.alias ?? "");

  if (!schoolData) {
    return null;
  }

  async function handleAliasBlur() {
    const trimmed = alias.trim();
    if (trimmed === (schoolData?.alias ?? "")) return;

    await submitUpdateAlias({ data: { alias: trimmed || null } });
    await router.invalidate();
  }

  /** Effective value: unsaved crop or saved logo from API (so clicking preview opens cropper). */
  const effectiveLogoValue = logoValue ?? schoolData.logo ?? null;

  async function handleLogoChange(value: ImageCropValue | null) {
    setLogoValue(value); // optimistic — preview updates immediately
    if (!value) return;

    try {
      await submitUpdateLogo({ data: value });
    } catch {
      // toastErrorMiddleware already surfaced the error; drop the optimistic preview
      setLogoValue(null);
      return;
    }
    await router.invalidate(); // loader now reflects the new logo…
    setLogoValue(null); // …so dropping the optimistic value can't flip back to the old one
  }

  return (
    <>
      <Page.MobileHeader />
      <Page.Container container="sm">
        <Page.Body>
          <form className="w-full" onSubmit={(e) => e.preventDefault()}>
            <div className="max-w-250">
              <FieldGroup>
                <Field orientation="responsive-fixed">
                  <FieldContent>
                    <FieldLabel>{m["common.fields.name"]()}</FieldLabel>
                  </FieldContent>
                  <div className="flex flex-col gap-1">
                    <Input value={schoolData.name} readOnly disabled className="bg-muted/50" />
                  </div>
                </Field>

                <Field orientation="responsive-fixed">
                  <FieldContent>
                    <FieldLabel>{m["school.info.alias"]()}</FieldLabel>
                  </FieldContent>
                  <div className="flex flex-col gap-1">
                    {canEdit ? (
                      <Input
                        value={alias}
                        onChange={(e) => setAlias(e.target.value)}
                        onBlur={handleAliasBlur}
                        maxLength={150}
                        placeholder={m["school.info.alias_placeholder"]()}
                      />
                    ) : (
                      <Input value={alias} readOnly disabled className="bg-muted/50" />
                    )}
                  </div>
                </Field>

                <Field orientation="responsive-fixed">
                  <FieldContent>
                    <FieldLabel>{m["brin.brin"]()}</FieldLabel>
                  </FieldContent>
                  <div className="flex flex-col gap-1">
                    <Input value={schoolData.brin} readOnly disabled className="bg-muted/50" />
                  </div>
                </Field>

                {schoolData.bestuursnummer && (
                  <Field orientation="responsive-fixed">
                    <FieldContent>
                      <FieldLabel>{m["school.info.foundation"]()}</FieldLabel>
                    </FieldContent>
                    <div className="flex flex-col gap-1">
                      <Input
                        value={`${schoolData.bestuursnaam} (${schoolData.bestuursnummer})`}
                        readOnly
                        disabled
                        className="bg-muted/50"
                      />
                    </div>
                  </Field>
                )}

                <Field orientation="responsive-fixed">
                  <FieldContent>
                    <FieldLabel>{m["school.info.phone"]()}</FieldLabel>
                  </FieldContent>
                  <div className="flex flex-col gap-1">
                    <Input
                      value={schoolData.phoneNumber}
                      readOnly
                      disabled
                      className="bg-muted/50"
                    />
                  </div>
                </Field>

                <Field orientation="responsive-fixed">
                  <FieldContent>
                    <FieldLabel>{m["common.fields.adres"]()}</FieldLabel>
                  </FieldContent>
                  <div className="flex flex-col gap-1">
                    <Input
                      value={`${schoolData.address} ${schoolData.houseNumber}, ${schoolData.postalCode} ${schoolData.city}`}
                      readOnly
                      disabled
                      className="bg-muted/50"
                    />
                  </div>
                </Field>

                <Field orientation="responsive-fixed">
                  <FieldContent>
                    <FieldLabel>Logo</FieldLabel>
                  </FieldContent>
                  <div className="flex flex-col gap-1">
                    <ImageCropperField
                      value={effectiveLogoValue}
                      onChange={handleLogoChange}
                      previewImageId={schoolData.logo?.croppedFileId}
                      placeholder="Kies logo"
                      cropDialogTitle="Logo bijsnijden"
                      changeImageLabel={m["school.info.wijzig_logo"]()}
                    />
                  </div>
                </Field>
              </FieldGroup>
            </div>
          </form>
        </Page.Body>
      </Page.Container>
    </>
  );
}
